/**
 * Product Feature Blocks JavaScript
 * Handles scroll animations and lazy loading for feature images.
 *
 * Uses IntersectionObserver for performance-efficient scroll detection.
 * Progressive enhancement - works without JavaScript.
 */

class ProductFeatureBlocks {
  constructor() {
    this.blocks = document.querySelectorAll('.feature-block[data-animate]');
    this.observer = null;

    if (this.blocks.length > 0) {
      this.init();
    }
  }

  init() {
    // Check if IntersectionObserver is supported
    if (!('IntersectionObserver' in window)) {
      // Fallback: show all blocks immediately
      this.blocks.forEach(block => {
        block.classList.add('is-visible');
      });
      return;
    }

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      // Respect user preference: show all blocks immediately
      this.blocks.forEach(block => {
        block.classList.add('is-visible');
      });
      return;
    }

    // Set up IntersectionObserver
    this.setupObserver();
    this.observeBlocks();
  }

  setupObserver() {
    const options = {
      root: null, // viewport
      rootMargin: '0px 0px -100px 0px', // Trigger 100px before entering viewport
      threshold: 0.1 // 10% of element visible
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Element is in viewport
          entry.target.classList.add('is-visible');

          // Stop observing this element (one-time animation)
          this.observer.unobserve(entry.target);
        }
      });
    }, options);
  }

  observeBlocks() {
    this.blocks.forEach(block => {
      this.observer.observe(block);
    });
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductFeatureBlocks();
  });
} else {
  new ProductFeatureBlocks();
}

// Handle Shopify Theme Editor events
if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    if (event.detail.sectionId && event.target.querySelector('[data-section-type="product-feature-blocks"]')) {
      new ProductFeatureBlocks();
    }
  });
}
